import { IndependentAction, type TypeInstanceMap } from '@lighthouse/core'
import type { FlowNode, VariableTree2 } from '@lighthouse/shared'
import {
    getFilterControllerVariableOption,
    getFormDatasourceOptions,
    getImageVariableOption,
    getInputInnerTypeMatch,
    getInputVariableOption,
    getUpstreamActionsOptions,
    getUpstreamNodesOptions,
    getViewVariableOption,
    getVirtualFieldByInnerType,
    NodeFieldPreview,
    OUTPUT_SINGLE_DATA_NODES,
    placeholderFromInnerType,
    useAtomAction,
    useAtomData,
    WorkflowType
} from '@lighthouse/shared'
import type { JSONContent } from '@tiptap/core'
import { find } from 'rambda'
import React, { useCallback, useDeferredValue, useMemo } from 'react'

import { availableFieldBlocksAtom, availableFilterBlocksAtom, blockHighlightAtom, lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

export type LoopInfo = {
    open: boolean
    name: string
}

interface SettingParamProps {
    value?: JSONContent
    innerType: TypeInstanceMap
    allParentNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    onChange: (val: JSONContent) => void
}

export const SettingParam: React.FC<SettingParamProps> = ({
    value,
    innerType: paramInnerType,
    allParentNodes,
    allParentActions,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const disabledWithVersion = useIsDisabledWithVersion()
    const { type } = useFlowContainerContext()
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)
    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )

    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    const parentNodes = useMemo(() => {
        return allParentNodes?.filter(node => OUTPUT_SINGLE_DATA_NODES.has(node.data.nodeType))
    }, [allParentNodes])

    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    const { viewType, viewDsId, formDsId, isImage } = useActionAdderDepParams()
    const isInFlow = !!type
    const virtualField = useMemo(() => getVirtualFieldByInnerType(paramInnerType), [paramInnerType])
    const workflowList = useAtomData(workflowListAtom)
    const { customViewOption } = useVariableCustomViewOption()

    const imageOption = useMemo(() => {
        if (isImage) {
            return getImageVariableOption()
        }
    }, [isImage])

    const formOption = useMemo(() => {
        if (formDsId && !isInFlow) {
            return getFormDatasourceOptions({
                dataSource: find(ds => ds.id === formDsId, dataSourceList)
            })
        }
    }, [dataSourceList, formDsId, isInFlow])

    const viewOptions = useMemo(() => {
        if (isInFlow) {
            return
        }

        const options: VariableTree2[] = []
        if (viewDsId) {
            const viewOption = getViewVariableOption({
                dataSource: find(ds => ds.id === viewDsId, dataSourceList),
                viewType
            })
            if (viewOption) {
                options.push(viewOption)

                return options
            }
        }
        if (customViewOption) {
            options.push(customViewOption)
        }
        return options
    }, [isInFlow, customViewOption, dataSourceList, viewType, viewDsId])

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => (isInFlow ? '' : s?.pageId || ''), [isInFlow])
    )
    const pageOptions = useGetPageParameterAndVariableOptions({
        pageId,
        includeDeviceId: type !== WorkflowType.AUTOMATION && type !== WorkflowType.SUB_PROCESS
    })
    const userOption = useGetUserRecordOptions()
    const inputOption = useMemo(() => {
        if (isInFlow) {
            return
        }
        if (availableFieldBlocksInfo.length === 0) {
            return
        }
        return getInputVariableOption(availableFieldBlocksInfo, dataSourceList, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        })
    }, [dataSourceList, availableFieldBlocksInfo, handleMouseEnter, handleMouseLeave, isInFlow])

    const filterOption = useMemo(() => {
        if (availableFilterBlocksInfo.length === 0 || isInFlow) {
            return
        }
        return getFilterControllerVariableOption(availableFilterBlocksInfo, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        })
    }, [availableFilterBlocksInfo, handleMouseEnter, handleMouseLeave, isInFlow])

    const upstreamOptions = useMemo(() => {
        if (isInFlow) {
            return getUpstreamNodesOptions({
                workflowList,
                upstreamNodes: parentNodes,
                allUpstreamNodes: allParentNodes,
                dataSourceList
            })
        }
        return getUpstreamActionsOptions({
            workflowList,
            allParentActions: allParentActions || [],
            dataSourceList
        })
    }, [allParentActions, allParentNodes, dataSourceList, isInFlow, parentNodes, workflowList])

    const placeholder = placeholderFromInnerType[virtualField?.innerType || 'TEXT']

    return (
        <NodeFieldPreview
            innerTypes={getInputInnerTypeMatch(virtualField?.innerType)}
            disabled={disabledWithVersion}
            autoHeight
            // onChange={onChange}
            style={{ width: 180 }}
            onBlur={onChange}
            value={value}
            placeholder={placeholder}
            field={virtualField}
            pageOptions={pageOptions}
            upstreamOptions={upstreamOptions}
            inputOption={inputOption}
            filterOption={filterOption}
            userOption={userOption}
            viewOptions={viewOptions}
            formOption={formOption}
            imageOption={imageOption}
            dataSourceList={dataSourceList}
        />
    )
}
